import React from 'react'
import { Grid, Stack, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { i18nLocationName } from 'types'
import { useAuthStore } from 'store'
import { PropertyAttributeEnum } from 'types/enums'

import ListingName from '../ListingName'
import ListingDelete from '../ListingDelete'
import { AdminPropertyAdWithList } from '../types'

interface Props {
  propertyAd: AdminPropertyAdWithList
  matchesSM: boolean
  matchesTablet: boolean
  onUpdate: (
    name: string | null,
    notes: string | null,
    propertyAdAlternativeId: string | null
  ) => void
  listId: number | null
  isExpanded: boolean
  onDelete: () => void
}

function ListingDescription(props: Props) {
  const { propertyAd, matchesSM, onUpdate, listId, onDelete, isExpanded } =
    props

  const { t } = useTranslation()

  const locale = useAuthStore((state) => state.locale)

  const LOCALISED_LOCATION_NAME = React.useMemo(() => {
    return propertyAd?.properties?.[0]?.location?.searchLocation?.i18nLocationName?.find(
      (item) => item.locale === locale
    ) as i18nLocationName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const PROPERTY_TYPE = React.useMemo(() => {
    return propertyAd?.properties?.[0]?.propertyAttributes?.find((item) =>
      [1, 4].includes(propertyAd.adCategory.id)
        ? item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_TYPE
        : [2, 5].includes(propertyAd.adCategory.id)
        ? item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_COMMERCIAL_TYPE
        : item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_LAND_TYPE
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack direction="column" rowGap={'7px'}>
      <Grid
        container
        direction="row"
        justifyContent={'space-between'}
        sx={{ mb: '4px', flexWrap: 'nowrap' }}
      >
        <Grid item xs>
          <ListingName
            onUpdate={onUpdate}
            name={propertyAd?.favorite?.name ?? ''}
            notes={propertyAd?.favorite?.notes || null}
            propertyAdAlternativeId={propertyAd?.favorite?.entityId || null}
            listId={listId}
            propertyAd={propertyAd}
            matchesSM={matchesSM}
          />
        </Grid>
        {isExpanded ? (
          <Grid item xs="auto">
            <div onClick={(e) => (matchesSM ? undefined : e.stopPropagation())}>
              <ListingDelete
                listId={listId}
                propertyAd={propertyAd}
                onDelete={onDelete}
                isExpanded={isExpanded}
                listName={propertyAd?.favorite?.name ?? ''}
              />
            </div>
          </Grid>
        ) : null}
      </Grid>
      <Box
        sx={{
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '16px',
        }}
      >
        {`${t(
          `enums.${PROPERTY_TYPE?.type.description}.${PROPERTY_TYPE?.valueText}.base`
        )} ${
          propertyAd.properties?.[0]?.squareMeters
            ? t('generics.area', {
                count: Number(
                  propertyAd?.properties?.[0]?.squareMeters.toFixed(2)
                ),
              })
            : '-'
        }`}
      </Box>
      <Box
        sx={{
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '16px',
        }}
      >
        {LOCALISED_LOCATION_NAME?.locationPath?.replaceAll('/', ',')}
      </Box>
      <Box
        sx={{
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          {`${
            propertyAd.salesPrice
              ? t('generics.currency', {
                  count: Number(propertyAd.salesPrice.toFixed(2)),
                })
              : '-'
          }`}
        </div>

        <div>
          {`${
            propertyAd.salesPricePerSqrMeter &&
            propertyAd.salesPricePerSqrMeter > 10
              ? t('generics.priceToArea', {
                  count: Math.trunc(Number(propertyAd.salesPricePerSqrMeter)),
                })
              : propertyAd.salesPricePerSqrMeter &&
                propertyAd.salesPricePerSqrMeter < 10
              ? t('generics.priceToArea', {
                  count: Number(propertyAd.salesPricePerSqrMeter.toFixed(2)),
                })
              : '-'
          }`}
        </div>
      </Box>
    </Stack>
  )
}

export default ListingDescription
